import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from '../components/Layout';
// import { API, BASE_URL } from "../api";

const BASE_URL = API;
// const BASE_URL    = 'http://localhost:5000';
// const BASE_URL = 'http://192.168.0.57:5000';
const BOOKING_PFX = 'BK';
const CURRENCY    = 'NZD';

const fmt = (n) => parseFloat(n || 0).toFixed(2);

export default function ManageOnlineBooking() {
  const navigate = useNavigate();

  const [cars,      setCars]      = useState([]);
  const [status,    setStatus]    = useState('0');
  const [period,    setPeriod]    = useState('0');
  const [fromDate,  setFromDate]  = useState('');
  const [toDate,    setToDate]    = useState('');
  const [carId,     setCarId]     = useState('');
  const [search,    setSearch]    = useState('');
  const [archive,   setArchive]   = useState(0);

  const [data,       setData]       = useState([]);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);
  const [loading,    setLoading]    = useState(false);
  const [selected,   setSelected]   = useState([]);

  const [viewBooking, setViewBooking] = useState(null);

  useEffect(() => { if (!localStorage.getItem('token')) navigate('/login'); }, []);
  useEffect(() => { API.get('/online-bookings/cars').then(r => setCars(r.data)).catch(() => {}); }, []);

  const buildParams = useCallback((pg = page) => {
    const p = { page: pg, limit: 10, archive };
    if (!fromDate && !toDate && !carId && !search) {
      p.status = status;
      if (period !== '0') p.period = period;
    }
    if (fromDate && toDate) { p.from_date = fromDate; p.to_date = toDate; }
    if (carId)  p.car_id = carId;
    if (search) p.search = search;
    return p;
  }, [page, status, period, fromDate, toDate, carId, search, archive]);

  const fetchData = useCallback((pg = page) => {
    setLoading(true); setSelected([]);
    API.get('/online-bookings', { params: buildParams(pg) })
      .then(r => { setData(r.data.data); setTotalPages(r.data.totalPages); setTotal(r.data.total); })
      .catch(() => toast.error('Failed to load bookings'))
      .finally(() => setLoading(false));
  }, [buildParams, page]);

  useEffect(() => { fetchData(page); }, [page, archive]);

  const doSearch  = () => { setPage(1); fetchData(1); };
  const resetAll  = () => { setStatus('0'); setPeriod('0'); setFromDate(''); setToDate(''); setCarId(''); setSearch(''); setPage(1); setTimeout(() => fetchData(1), 50); };

  const toggleAll = () => setSelected(selected.length === data.length ? [] : data.map(r => r.bookingid));
  const toggleOne = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const handleBulkDelete = async () => {
    if (!selected.length) { toast.warning('Select bookings first'); return; }
    if (!window.confirm(`Delete ${selected.length} booking(s)?`)) return;
    try { await API.delete('/online-bookings/bulk/delete', { data: { ids: selected } }); toast.success('Deleted'); fetchData(page); }
    catch (err) { toast.error(err.response?.data?.error || 'Delete failed'); }
  };

  const handleBulkArchive = async () => {
    if (!selected.length) { toast.warning('Select bookings first'); return; }
    if (!window.confirm(`Archive ${selected.length} booking(s)?`)) return;
    try { await API.patch('/online-bookings/bulk/archive', { ids: selected }); toast.success('Archived'); fetchData(page); }
    catch (err) { toast.error(err.response?.data?.error || 'Archive failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this booking?')) return;
    try { await API.delete(`/online-bookings/${id}`); toast.success('Booking deleted'); fetchData(page); }
    catch (err) { toast.error(err.response?.data?.error || 'Delete failed'); }
  };

  const handleAccept = async (id) => {
    try { await API.patch(`/online-bookings/${id}/accept`); toast.success('Booking accepted'); fetchData(page); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Reject this booking?')) return;
    try { await API.patch(`/online-bookings/${id}/reject`); toast.success('Booking rejected'); fetchData(page); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const handleView = async (id) => {
    setViewBooking({ loading: true });
    try { const r = await API.get(`/online-bookings/${id}`); setViewBooking(r.data); }
    catch { toast.error('Failed to load booking'); setViewBooking(null); }
  };

  const statusChip = (row) => {
    if (row.status === 0) return { label: 'Cancelled', bg: '#fff0f0', color: '#c0392b', border: '#fca5a5' };
    if (row.status === 2) return { label: 'Modified',  bg: '#fffbf0', color: '#b45309', border: '#fcd34d' };
    if (row.status === 1 && row.pay_status === 0) return { label: 'Unpaid', bg: '#f3f2ff', color: '#6C63FF', border: '#d4d0ff' };
    if (row.accept_reject === 1) return { label: 'Accepted', bg: '#f0fff6', color: '#1a9e5c', border: '#b8f0d0' };
    return { label: 'Pending', bg: '#f0f8ff', color: '#1a6eb5', border: '#cce5ff' };
  };

  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <Layout activePage="Manage Bookings">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div style={S.header}>
        <div>
          <h2 style={S.title}>{archive === 1 ? '📦 Booking Archive' : '📋 Manage Car Rental Bookings'}</h2>
          <p style={S.subtitle}>{total} {archive === 1 ? 'archived' : 'active'} booking{total !== 1 ? 's' : ''}</p>
        </div>
        <button style={{ ...S.archBtn, background: archive === 1 ? '#1a1d2e' : '#326F8C' }}
          onClick={() => { setArchive(a => a === 0 ? 1 : 0); setPage(1); }}>
          {archive === 1 ? '← Back to Active' : '📦 Show Archive'}
        </button>
      </div>

      {/* Filters */}
      {archive === 0 && (
        <div style={S.filterCard}>
          <div style={S.filterRow}>
            <span style={S.filterLabel}>Status</span>
            <select value={status} onChange={e => setStatus(e.target.value)} style={S.select}>
              <option value="0">Booked</option><option value="1">Cancelled</option>
              <option value="2">Modified</option><option value="3">Unpaid</option>
            </select>
            <button style={S.searchBtn} onClick={doSearch}>Search</button>
          </div>
          <div style={S.filterRow}>
            <span style={S.filterLabel}>Name / Booking Ref</span>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && doSearch()}
              placeholder="Name or booking number…" style={S.filterInput} />
            <button style={S.searchBtn} onClick={doSearch}>Search</button>
          </div>
          <div style={S.filterRow}>
            <span style={S.filterLabel}>Date Range</span>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={S.dateInput} />
            <span style={{ color: '#aaa', fontSize: 12 }}>to</span>
            <input type="date" value={toDate} min={fromDate} onChange={e => setToDate(e.target.value)} style={S.dateInput} />
            <button style={S.searchBtn} onClick={doSearch}>Search</button>
          </div>
          <div style={S.filterRow}>
            <span style={S.filterLabel}>Car</span>
            <select value={carId} onChange={e => setCarId(e.target.value)} style={S.select}>
              <option value="">— All Cars —</option>
              {cars.map(c => <option key={c.car_id} value={c.car_id}>{c.car_name}</option>)}
            </select>
            <button style={S.searchBtn} onClick={doSearch}>Search</button>
            <button style={S.resetBtn} onClick={resetAll}>Reset All</button>
          </div>
        </div>
      )}

      {/* Bulk actions */}
      <div style={S.bulkRow}>
        <span style={S.selInfo}>{selected.length > 0 ? `${selected.length} selected` : ''}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          {archive === 0 && <button style={S.archiveBtn} disabled={!selected.length} onClick={handleBulkArchive}>📦 Set Archive</button>}
          <button style={S.deleteBtn} disabled={!selected.length} onClick={handleBulkDelete}>🗑️ Delete</button>
        </div>
      </div>

      {/* Table */}
      <div style={S.card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr style={S.theadRow}>
                <th style={{ ...S.th, width: 36 }}>
                  <input type="checkbox" checked={data.length > 0 && selected.length === data.length}
                    onChange={toggleAll} style={{ accentColor: '#6C63FF' }} />
                </th>
                {['Booking Ref', 'Booking Date', 'Pick-up Date', 'Customer', 'Email', 'Mobile', 'Car', 'Status', 'Actions'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} style={S.emptyCell}>Loading…</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={10} style={S.emptyCell}>No records found</td></tr>
              ) : data.map(row => {
                const chip    = statusChip(row);
                const isSel   = selected.includes(row.bookingid);
                return (
                  <tr key={row.bookingid} style={{ ...S.tr, background: isSel ? '#f3f2ff' : 'transparent' }}>
                    <td style={S.td}><input type="checkbox" checked={isSel} onChange={() => toggleOne(row.bookingid)} style={{ accentColor: '#6C63FF' }} /></td>
                    <td style={S.td}><span style={S.refChip}>{BOOKING_PFX} {row.bookingid}</span></td>
                    <td style={{ ...S.td, fontSize: 12, color: '#777' }}>{fmtDate(row.online_bookingdate)}</td>
                    <td style={{ ...S.td, fontSize: 12, color: '#555' }}>{fmtDate(row.online_pickdate)}</td>
                    <td style={S.td}><span style={{ fontWeight: 600, fontSize: 13 }}>{row.online_title} {row.online_firstname} {row.online_lastname}</span></td>
                    <td style={{ ...S.td, fontSize: 12, color: '#666' }}>{row.online_emailid}</td>
                    <td style={S.td}><span style={S.mobileChip}>{row.online_mobileno}</span></td>
                    <td style={{ ...S.td, fontSize: 12 }}>{row.car_name || '—'}</td>
                    <td style={S.td}><span style={{ ...S.statusChip, background: chip.bg, color: chip.color, border: `1px solid ${chip.border}` }}>{chip.label}</span></td>
                    <td style={{ ...S.td, textAlign: 'right' }}>
                      <button style={S.viewBtn} onClick={() => handleView(row.bookingid)}>View</button>
                      <button style={S.delBtn}  onClick={() => handleDelete(row.bookingid)}>Delete</button>
                      {archive === 0 && row.status === 1 && row.pay_status === 1 && (
                        row.accept_reject === 1
                          ? <button style={S.acceptedBtn} onClick={() => handleReject(row.bookingid)}>✅ Accepted</button>
                          : <button style={S.pendingBtn}  onClick={() => handleAccept(row.bookingid)}>⏳ Accept</button>
                      )}
                      {archive === 0 && row.status === 2 && <span style={S.modTag}>Modified</span>}
                      {archive === 0 && row.status === 0 && <span style={S.canTag}>Cancelled</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={S.pagination}>
          <span style={S.paginInfo}>{total > 0 ? `Showing ${(page-1)*10+1}–${Math.min(page*10,total)} of ${total}` : ''}</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button style={S.pgBtn} disabled={page === 1} onClick={() => setPage(p => p-1)}>← Prev</button>
            <span style={S.paginInfo}>Page {page} / {totalPages || 1}</span>
            <button style={S.pgBtn} disabled={page === totalPages || totalPages === 0} onClick={() => setPage(p => p+1)}>Next →</button>
          </div>
        </div>
      </div>

      {/* ── Full View Modal ── */}
      {viewBooking && (
        <div style={S.overlay} onClick={() => setViewBooking(null)}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={S.modalHead}>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                  📋 {BOOKING_PFX} {viewBooking.bookingid || '…'}
                </h3>
                {viewBooking.pay_status === 1
                  ? <span style={{ ...S.payBadge, background: '#f0fff6', color: '#1a9e5c', border: '1px solid #b8f0d0' }}>✅ Paid</span>
                  : <span style={{ ...S.payBadge, background: '#fff0f0', color: '#c0392b', border: '1px solid #fca5a5' }}>❌ Not Paid</span>}
              </div>
              <button style={S.closeBtn} onClick={() => setViewBooking(null)}>✕</button>
            </div>

            {viewBooking.loading ? (
              <div style={{ padding: 60, textAlign: 'center', color: '#aaa' }}>Loading…</div>
            ) : (
              <div style={S.modalBody}>

                {/* Car image */}
                {viewBooking.car_image && (
                  <img src={`${BASE_URL}/${viewBooking.car_image}`} alt={viewBooking.car_name}
                    style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 12, marginBottom: 16 }} />
                )}

                {/* ── Customer Details ── */}
                <div style={S.section}>Customer Details</div>
                <div style={S.viewGrid}>
                  <InfoRow label="Booking Name"    value={`${viewBooking.online_title} ${viewBooking.online_firstname} ${viewBooking.online_lastname}`} />
                  <InfoRow label="Email"           value={viewBooking.online_emailid} />
                  <InfoRow label="Mobile"          value={viewBooking.online_mobileno} />
                  <InfoRow label="Telephone"       value={viewBooking.online_telephone} />
                  <InfoRow label="Gender"          value={viewBooking.online_gender} />
                  <InfoRow label="Address"         value={viewBooking.online_address} />
                  <InfoRow label="City"            value={viewBooking.online_city} />
                  <InfoRow label="Country"         value={viewBooking.online_country} />
                  <InfoRow label="Postcode"        value={viewBooking.online_postcode} />
                  <InfoRow label="DL Number"       value={viewBooking.dl_number} mono />
                  <InfoRow label="DL Expiry"       value={fmtDate(viewBooking.dl_expirydate)} />
                  <InfoRow label="Driver Age"      value={viewBooking.online_driverage ? `${viewBooking.online_driverage} Years` : null} />
                </div>

                {/* ── Booking Details ── */}
                <div style={S.section}>Booking Details</div>
                <div style={S.viewGrid}>
                  <InfoRow label="Car"             value={viewBooking.car_name} />
                  <InfoRow label="Car Group"       value={viewBooking.group_name} />
                  <InfoRow label="Passengers"      value={viewBooking.car_passgrno} />
                  <InfoRow label="Doors"           value={viewBooking.car_doors} />
                  <InfoRow label="Engine"          value={viewBooking.car_engcapacity} />
                  <InfoRow label="Air Conditioning" value={viewBooking.car_airconditionr ? 'Yes' : 'No'} />
                  <InfoRow label="Pick-up City"    value={viewBooking.pick_city_name} />
                  <InfoRow label="Pick-up Station" value={viewBooking.pick_station_name} />
                  <InfoRow label="Pick-up Date"    value={fmtDate(viewBooking.online_pickdate)} />
                  <InfoRow label="Pick-up Time"    value={viewBooking.online_picktime ? `${viewBooking.online_picktime}.00` : null} />
                  <InfoRow label="Return City"     value={viewBooking.return_city_name} />
                  <InfoRow label="Return Station"  value={viewBooking.return_station_name} />
                  <InfoRow label="Return Date"     value={fmtDate(viewBooking.online_returndate)} />
                  <InfoRow label="Return Time"     value={viewBooking.online_returntime ? `${viewBooking.online_returntime}.00` : null} />
                  <InfoRow label="Duration"        value={viewBooking.online_diffdate ? `${viewBooking.online_diffdate} Days` : null} />
                  <InfoRow label="Comments"        value={viewBooking.online_comments} />
                  <InfoRow label="Booking Date"    value={fmtDate(viewBooking.online_bookingdate)} />
                  {viewBooking.oldbookingid > 0 && (
                    <InfoRow label="Old Booking Ref" value={`${BOOKING_PFX} ${viewBooking.oldbookingid}`} />
                  )}
                </div>

                {/* ── Free Optional Extras ── */}
                {viewBooking.free_extras?.length > 0 && (
                  <>
                    <div style={S.section}>Free Optional Extras</div>
                    <table style={S.miniTable}>
                      <thead><tr><th style={S.mth}>Item Name</th><th style={S.mth}>Quantity</th></tr></thead>
                      <tbody>
                        {viewBooking.free_extras.map((x, i) => (
                          <tr key={i}><td style={S.mtd}>{x.name}</td><td style={S.mtd}>{x.qty}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {/* ── Paid Optional Extras ── */}
                {viewBooking.paid_extras?.length > 0 && (
                  <>
                    <div style={S.section}>Paid Optional Extras</div>
                    <table style={S.miniTable}>
                      <thead>
                        <tr>
                          <th style={S.mth}>Item Name</th>
                          <th style={S.mth}>Qty</th>
                          <th style={S.mth}>Rate</th>
                          <th style={{ ...S.mth, textAlign: 'right' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewBooking.paid_extras.map((x, i) => (
                          <tr key={i}>
                            <td style={S.mtd}>{x.name}</td>
                            <td style={S.mtd}>{x.qty}</td>
                            <td style={S.mtd}>
                              {fmt(x.rate)} {CURRENCY} × {x.rate_type === '/Once' ? '1 (once)' : `${x.days} days`}
                            </td>
                            <td style={{ ...S.mtd, textAlign: 'right', fontWeight: 700 }}>
                              {fmt(x.subtotal)} {CURRENCY}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {/* ── Insurance ── */}
                {viewBooking.ins_extras?.length > 0 && (
                  <>
                    <div style={S.section}>Insurance</div>
                    <table style={S.miniTable}>
                      <thead>
                        <tr>
                          <th style={S.mth}>Insurance Item</th>
                          <th style={S.mth}>Rate</th>
                          <th style={{ ...S.mth, textAlign: 'right' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewBooking.ins_extras.map((x, i) => (
                          <tr key={i}>
                            <td style={S.mtd}>{x.name}</td>
                            <td style={S.mtd}>{fmt(x.rate)} {CURRENCY} × {x.days} days</td>
                            <td style={{ ...S.mtd, textAlign: 'right', fontWeight: 700 }}>{fmt(x.total)} {CURRENCY}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {/* ── Rate Chart ── */}
                <div style={S.section}>Rate Chart</div>
                <table style={S.miniTable}>
                  <thead>
                    <tr>
                      <th style={S.mth}>Quote</th>
                      <th style={S.mth}>Calculation</th>
                      <th style={{ ...S.mth, textAlign: 'right' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <RateRow label="Online Price"
                      calc={`${fmt(viewBooking.online_onlineprice)} × ${viewBooking.online_diffdate} Days`}
                      amount={viewBooking.online_onlineprice * viewBooking.online_diffdate} />
                    {viewBooking.online_picknightcharge > 0 && <RateRow label="Pick Night Charge"    calc={`${fmt(viewBooking.online_picknightcharge)} × 1`} amount={viewBooking.online_picknightcharge} />}
                    {viewBooking.online_dropoffnightcharge > 0 && <RateRow label="Drop Night Charge"  calc={`${fmt(viewBooking.online_dropoffnightcharge)} × 1`} amount={viewBooking.online_dropoffnightcharge} />}
                    {viewBooking.online_pickcharge > 0 && <RateRow label="Pick-up Charges"  calc={`${fmt(viewBooking.online_pickcharge)} × 1`} amount={viewBooking.online_pickcharge} />}
                    {viewBooking.online_dropcharge > 0 && <RateRow label="Drop-off Charges" calc={`${fmt(viewBooking.online_dropcharge)} × 1`} amount={viewBooking.online_dropcharge} />}
                    {viewBooking.delay_charge > 0 && <RateRow label="Delay Charge"       calc={`${fmt(viewBooking.delay_charge)} × 1`} amount={viewBooking.delay_charge} />}
                    {viewBooking.oneway_rent_chrg > 0 && <RateRow label="One Way Fee"      calc={`${fmt(viewBooking.oneway_rent_chrg)} × 1`} amount={viewBooking.oneway_rent_chrg} />}
                  </tbody>
                </table>

                {/* ── Price Summary ── */}
                <div style={S.section}>Price Summary</div>
                <div style={S.summaryBox}>
                  <SummaryRow label="Estimated Total"         value={`${fmt(viewBooking.est_total)} ${CURRENCY}`} />
                  <SummaryRow label={`Local Tax [${viewBooking.tax_rate}%]`} value={`${fmt(viewBooking.local_tax)} ${CURRENCY}`} />
                  {viewBooking.discount_price > 0 && (
                    <SummaryRow label={`Discount [${viewBooking.discount_prcnt}%]`} value={`-${fmt(viewBooking.discount_price)} ${CURRENCY}`} red />
                  )}
                  {viewBooking.cash_discamt > 0 && (
                    <SummaryRow label={`Cash Discount [${viewBooking.cash_discpercent}%]`} value={`-${fmt(viewBooking.cash_discamt)} ${CURRENCY}`} red />
                  )}
                  <SummaryRow label="Total Amount"            value={`${fmt(viewBooking.total_amount)} ${CURRENCY}`} bold />
                  <SummaryRow label="Round Price"             value={`${fmt(Math.round(viewBooking.total_amount))} ${CURRENCY}`} bold />
                  {viewBooking.adv_paid > 0 && (
                    <>
                      <SummaryRow label="Advance Paid"        value={`${fmt(viewBooking.adv_paid)} ${CURRENCY}`} green />
                      <SummaryRow label="Balance to Pay"      value={`${fmt(viewBooking.balance_to_pay)} ${CURRENCY}`} bold />
                    </>
                  )}
                </div>

              </div>
            )}

            <div style={S.modalFoot}>
              <button style={S.submitBtn} onClick={() => setViewBooking(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

/* ── Helper components ── */
function InfoRow({ label, value, mono }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11, color: '#aaa', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#333', fontWeight: 500, fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</div>
    </div>
  );
}

function RateRow({ label, calc, amount }) {
  return (
    <tr>
      <td style={{ padding: '8px 12px', fontSize: 13, color: '#c0392b', borderBottom: '1px solid #f8f8f8' }}>{label}</td>
      <td style={{ padding: '8px 12px', fontSize: 12, color: '#888', borderBottom: '1px solid #f8f8f8' }}>{calc}</td>
      <td style={{ padding: '8px 12px', fontSize: 13, fontWeight: 700, color: '#c0392b', textAlign: 'right', borderBottom: '1px solid #f8f8f8' }}>
        {parseFloat(amount || 0).toFixed(2)} {CURRENCY}
      </td>
    </tr>
  );
}

function SummaryRow({ label, value, bold, red, green }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '8px 0', borderBottom: '1px solid #f5f5f5' }}>
      <span style={{ fontSize: 13, color: '#555', fontWeight: bold ? 700 : 400 }}>{label}</span>
      <span style={{ fontSize: bold ? 16 : 13, fontWeight: bold ? 800 : 600,
        color: red ? '#c0392b' : green ? '#1a9e5c' : '#c0392b' }}>{value}</span>
    </div>
  );
}

/* ─── Styles ─── */
const S = {
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title:       { margin: 0, fontSize: 24, fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' },
  subtitle:    { margin: '4px 0 0', fontSize: 13, color: '#999' },
  archBtn:     { padding: '10px 20px', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' },

  filterCard:  { background: '#fff', borderRadius: 16, padding: '20px', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', marginBottom: 16 },
  filterRow:   { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap', paddingBottom: 14, borderBottom: '1px solid #f5f5f5' },
  filterLabel: { fontSize: 12, fontWeight: 700, color: '#6C63FF', minWidth: 150, textTransform: 'uppercase', letterSpacing: '0.5px' },
  select:      { padding: '8px 14px', border: '1.5px solid #e0e0e0', borderRadius: 10, fontSize: 13, fontFamily: 'inherit', color: '#333', outline: 'none', background: '#fff', minWidth: 180 },
  filterInput: { padding: '8px 14px', border: '1.5px solid #e0e0e0', borderRadius: 10, fontSize: 13, fontFamily: 'inherit', color: '#333', outline: 'none', minWidth: 280 },
  dateInput:   { padding: '8px 12px', border: '1.5px solid #e0e0e0', borderRadius: 10, fontSize: 13, fontFamily: 'inherit', color: '#333', outline: 'none' },
  searchBtn:   { padding: '8px 18px', background: 'linear-gradient(135deg,#6C63FF,#43BCCD)', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' },
  resetBtn:    { padding: '8px 16px', background: '#f5f5f5', color: '#666', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },

  bulkRow:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8 },
  selInfo:     { fontSize: 13, fontWeight: 600, color: '#6C63FF' },
  archiveBtn:  { padding: '8px 18px', background: '#326F8C', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' },
  deleteBtn:   { padding: '8px 18px', background: '#ff4444', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' },

  card:        { background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', overflow: 'hidden' },
  table:       { width: '100%', borderCollapse: 'collapse' },
  theadRow:    { background: '#fafafa' },
  th:          { padding: '12px 12px', fontSize: 11, fontWeight: 700, color: '#999', textAlign: 'left', borderBottom: '2px solid #f0f0f0', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  tr:          { transition: 'background 0.12s' },
  td:          { padding: '12px 12px', fontSize: 13, color: '#333', borderBottom: '1px solid #f8f8f8' },
  emptyCell:   { textAlign: 'center', padding: 40, color: '#bbb', fontSize: 14 },

  refChip:     { background: '#f3f2ff', color: '#6C63FF', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700 },
  mobileChip:  { background: '#f0f8ff', color: '#1a6eb5', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600 },
  statusChip:  { borderRadius: 20, padding: '4px 12px', fontSize: 11, fontWeight: 700 },
  payBadge:    { borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700, marginLeft: 8 },

  viewBtn:     { marginRight: 5, background: '#f0fff6', color: '#1a9e5c', border: '1px solid #b8f0d0', padding: '4px 10px', borderRadius: 20, cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: 'inherit' },
  delBtn:      { marginRight: 5, background: '#fff2f5', color: '#FF6584', border: '1px solid #ffd0d8', padding: '4px 10px', borderRadius: 20, cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: 'inherit' },
  acceptedBtn: { marginRight: 5, background: '#f3f2ff', color: '#6C63FF', border: '1px solid #d4d0ff', padding: '4px 10px', borderRadius: 20, cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: 'inherit' },
  pendingBtn:  { marginRight: 5, background: '#f0fff6', color: '#1a9e5c', border: '1px solid #b8f0d0', padding: '4px 10px', borderRadius: 20, cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: 'inherit' },
  modTag:      { fontSize: 11, fontWeight: 700, background: '#fffbf0', color: '#b45309', borderRadius: 20, padding: '3px 10px', border: '1px solid #fcd34d' },
  canTag:      { fontSize: 11, fontWeight: 700, background: '#fff0f0', color: '#c0392b', borderRadius: 20, padding: '3px 10px', border: '1px solid #fca5a5' },

  pagination:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: '1px solid #f0f0f0' },
  paginInfo:   { fontSize: 12, color: '#999' },
  pgBtn:       { padding: '5px 14px', background: '#f3f2ff', color: '#6C63FF', border: '1px solid #d4d0ff', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' },

  overlay:     { position: 'fixed', inset: 0, background: 'rgba(10,10,30,0.6)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' },
  modalBox:    { background: '#fff', borderRadius: 20, width: 720, maxWidth: '96vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.25)' },
  modalHead:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 },
  modalBody:   { padding: '20px 22px', overflowY: 'auto', flex: 1 },
  modalFoot:   { display: 'flex', justifyContent: 'flex-end', padding: '14px 22px', borderTop: '1px solid #f0f0f0', flexShrink: 0 },
  closeBtn:    { background: '#ff6b6b22', border: 'none', color: '#ff6b6b', borderRadius: '50%', width: 30, height: 30, cursor: 'pointer', fontWeight: 800, fontSize: 14 },
  submitBtn:   { padding: '9px 22px', background: 'linear-gradient(135deg,#6C63FF,#43BCCD)', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' },

  section:     { fontSize: 11, fontWeight: 800, color: '#6C63FF', textTransform: 'uppercase', letterSpacing: '0.7px', borderBottom: '1.5px solid #f3f2ff', paddingBottom: 6, marginBottom: 12, marginTop: 20 },
  viewGrid:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 20px', marginBottom: 4 },

  miniTable:   { width: '100%', borderCollapse: 'collapse', marginBottom: 4 },
  mth:         { padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#999', textAlign: 'left', borderBottom: '2px solid #f0f0f0', textTransform: 'uppercase', letterSpacing: '0.4px' },
  mtd:         { padding: '8px 12px', fontSize: 13, color: '#c0392b', borderBottom: '1px solid #f8f8f8' },

  summaryBox:  { background: '#fafafa', borderRadius: 12, padding: '4px 16px', marginBottom: 4 },
};
